/**
 * Voice designer: the Create tab of the Voice & Speech panel.
 *
 * Describe how a voice sounds, generate a few takes, and pick the one to
 * speak with. A pick publishes the take as one of Your voices (the same
 * store an uploaded clip uses), so it is selectable everywhere at once;
 * picking a different take later replaces that voice's clip in place.
 *
 * Two things were learned making the first voices, and the form says both
 * where they are needed:
 *   - the model follows a description of the SOUND (age, pitch, texture,
 *     pace), and does not follow a role ("like a wizard") or an accent's
 *     name ("Boston");
 *   - an accent does not come through, by name or spelled into the words
 *
 * Fields save themselves a moment after typing stops. Generating is a job
 * on the server; this polls it and shows each take as it lands.
 */

import { API_BASE, state } from './state.js';
import { getHeaders } from './api.js';
import { getAuthToken } from './auth.js';

let root = null;
let onVoicesChanged = () => {};
let data = null;              // last GET /voice/designs
let currentId = null;         // the design in the editor
let showDefaults = false;     // the defaults pane instead of a design
let job = null;               // the running generate job, if any
let jobTimer = null;
let saveTimer = null;
let pending = {};             // unsaved field edits for currentId
let saveNote = '';
let playing = null;           // { takeId, audio }
let confirmDelete = false;
let errorLine = '';
let writing = false;          // a chat model is writing the description
let writeNote = '';           // who wrote it, shown under the idea
let writeUndo = null;         // { id, description, text } from before the last write

const STARTERS = [
    {
        name: 'Old Wizard',
        description: 'A very old man, around ninety, with a deep, guttural, throaty voice that growls up from the chest. Rough and gravelly, with heavy vocal fry and a dry rasp, breathy at the ends of phrases. He speaks very slowly, with long pauses, low and quiet.',
    },
    {
        name: 'Elf Queen',
        description: 'A regal woman with a clear, cool, melodic voice, high and airy but commanding. She speaks with unhurried grace and a formal, slightly distant elegance.',
    },
    {
        name: 'Dockside Tough',
        description: 'A man in his fifties. Low, gruff, gravelly voice. Brisk and blunt.',
    },
];

const GEAR = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h0a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v0a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/></svg>';

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escAttr = (s) => esc(s).replace(/"/g, '&quot;');

function authQuery() {
    const token = getAuthToken();
    if (token) return `?token=${encodeURIComponent(token)}`;
    if (state.containerMode && state.containerToken) return `?u=${encodeURIComponent(state.containerToken)}`;
    if (state.capsuleMode && state.capsuleToken) return `?c=${encodeURIComponent(state.capsuleToken)}`;
    return '';
}

async function call(method, path, body) {
    const res = await fetch(`${API_BASE}/voice/designs${path}`, {
        method,
        headers: getHeaders(),
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    let json = null;
    try { json = await res.json(); } catch { /* an empty body is fine */ }
    if (!res.ok) throw new Error(json?.detail || `Request failed (${res.status})`);
    return json;
}

function adopt(payload) {
    if (payload?.designs) data = payload;
    if (currentId && !design()) currentId = null;
}

const design = () => data?.designs?.find((d) => d.id === currentId) || null;

// ─── Public ──────────────────────────────────────────────────────────

export function initVoiceDesigner(container, opts = {}) {
    root = container;
    onVoicesChanged = opts.onVoicesChanged || onVoicesChanged;
    root.addEventListener('click', onClick);
    root.addEventListener('input', onInput);
    root.addEventListener('change', onInput);
    root.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target.id === 'vd-idea') { e.preventDefault(); describeIdea(); }
    });
}

/** Called each time the tab is shown: the list may have changed elsewhere. */
export async function showVoiceDesigner() {
    if (!root) return;
    if (!data) root.innerHTML = '<div class="vd-loading">Loading your voices…</div>';
    try {
        adopt(await call('GET', ''));
        errorLine = '';
        if (!currentId && data.designs.length) currentId = data.designs[0].id;
    } catch (e) {
        errorLine = e.message;
        data = data || { designs: [], settings: {}, designer: { available: false, reason: e.message }, limits: {}, languages: ['English'] };
    }
    render();
}

/** Leaving the tab or closing the panel: stop audio, flush edits. */
export function hideVoiceDesigner() {
    stopPlaying();
    flushSave();
}

// ─── Rendering ───────────────────────────────────────────────────────

function render() {
    if (!root || !data) return;
    const keep = captureFocus();
    // Redrawing replaces every element, and a replaced scroller starts at the
    // top. Put each one back where it was, or deleting a take from the bottom
    // of the list throws the reader to the top of the page.
    const scrolls = [];
    for (let el = root; el; el = el.parentElement) if (el.scrollTop) scrolls.push([el, el.scrollTop]);
    const mainTop = root.querySelector('.vd-main')?.scrollTop || 0;
    const listTop = root.querySelector('.vd-list')?.scrollTop || 0;
    // With nothing made yet there is nothing to list: the rail would be an
    // empty column pushing the invitation off centre, with a second "New
    // voice" button in it. The invitation takes the whole width instead.
    const bare = !data.designs.length && !showDefaults;
    root.innerHTML = bare
        ? `${renderBanner()}<div class="vd-body vd-body-bare"><section class="vd-main">${renderEmpty()}</section></div>`
        : `
        ${renderBanner()}
        <div class="vd-body">
            <aside class="vd-rail">
                <button class="vd-new" data-act="new">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    New voice
                </button>
                <div class="vd-list" role="list">${renderList()}</div>
                <button class="vd-defaults-btn${showDefaults ? ' active' : ''}" data-act="defaults">
                    ${GEAR}
                    Defaults
                </button>
            </aside>
            <section class="vd-main">${showDefaults ? renderDefaults() : (design() ? renderEditor() : renderEmpty())}</section>
        </div>`;
    const main = root.querySelector('.vd-main');
    if (main) main.scrollTop = mainTop;
    const list = root.querySelector('.vd-list');
    if (list) list.scrollTop = listTop;
    for (const [el, top] of scrolls) el.scrollTop = top;
    restoreFocus(keep);
}

function renderBanner() {
    const d = data.designer || {};
    if (errorLine) return `<div class="vd-banner vd-banner-err" role="alert">${esc(errorLine)}</div>`;
    if (!d.available) return `<div class="vd-banner" role="status">${esc(d.reason || 'The voice designer is not available.')}</div>`;
    if (d.reason) return `<div class="vd-banner" role="status">${esc(d.reason)}</div>`;
    return '';
}

function renderList() {
    if (!data.designs.length) return '<p class="vd-list-empty">No voices yet.</p>';
    return data.designs.map((d) => {
        const n = d.takes.length;
        const sub = d.voice_id ? 'In Your voices' : (n ? `${n} take${n === 1 ? '' : 's'}` : 'No takes yet');
        return `
            <button class="vd-item${d.id === currentId && !showDefaults ? ' active' : ''}" data-act="open" data-id="${d.id}" role="listitem">
                <span class="vd-item-name">${esc(d.name)}</span>
                <span class="vd-item-sub${d.voice_id ? ' live' : ''}">${sub}</span>
            </button>`;
    }).join('');
}

function renderEmpty() {
    return `
        <div class="vd-empty">
            <h3>Describe a voice and hear it</h3>
            <p>Write how a voice sounds, generate a few takes, and pick the one you like. It joins Your voices and can speak anything.</p>
            <div class="vd-starters">
                <span class="vd-label">Start from an example</span>
                ${STARTERS.map((s, i) => `<button class="vd-chip" data-act="starter" data-i="${i}">${esc(s.name)}</button>`).join('')}
            </div>
            <button class="vd-primary" data-act="new">New voice</button>
            ${data.designs.length ? '' : `<button class="vd-defaults-btn" data-act="defaults">${GEAR} Defaults for new voices</button>`}
        </div>`;
}

function renderEditor() {
    const d = design();
    const lim = data.limits || {};
    const running = job && job.design_id === d.id && job.state === 'running';
    const canMake = data.designer?.available && !data.designer?.reason;
    const takesDefault = data.settings?.takes || 2;
    const count = Math.min(lim.takes_per_run || 6, Number(root.querySelector('#vd-count')?.value) || takesDefault);
    const text = pending.text ?? d.text;
    const accents = data.accents || [];
    const chosenAccent = pending.accent ?? d.accent ?? '';
    const speakerChosen = accents.some((x) => x.id === chosenAccent);
    const blend = !!(pending.blend ?? d.blend);
    const direct = speakerChosen && !blend;
    const mood = direct ? '' : (pending.mood ?? d.mood ?? '');
    const moodPassage = (data.moods || []).find((m) => m.key === mood)?.passage || '';
    // A real speaker as the base voice needs no description, so the idea and
    // description are not shown at all for one.
    const descBlock = direct ? '' : `
            <label class="vd-field">
                <span class="vd-label">How it sounds</span>
                <textarea id="vd-description" data-field="description" rows="5" maxlength="${lim.description || 1200}"
                    placeholder="A very old man, around ninety. Thin, dry and raspy, a little shaky. He speaks very slowly, with long pauses.">${esc(pending.description ?? d.description)}</textarea>
                <span class="vd-hint">${direct ? 'The base voice below is a real speaker, so this is only used when Blend with the description is ticked.' : 'A few blunt words: age, pitch, texture, pace. Roles and accents do not come through; for an accent pick a base voice below.'}</span>
            </label>`;
    const ideaBlock = direct ? '' : `
        <div class="vd-idea">
            <label class="vd-label" for="vd-idea">Start from an idea <span class="vd-optional">optional</span></label>
            <div class="vd-idea-row">
                <input id="vd-idea" data-field="idea" maxlength="${lim.idea || 400}" value="${escAttr(pending.idea ?? d.idea ?? '')}"
                    placeholder="An old wizard. An elf queen. A sixties greaser. A tired dockworker." ${writing ? 'disabled' : ''}>
                <button class="vd-secondary${writing ? ' busy' : ''}" data-act="describe" ${writing ? 'disabled' : ''}>
                    ${writing ? '<span class="vd-spin" aria-hidden="true"></span>Writing' : 'Write the description'}
                </button>
            </div>
            <span class="vd-hint" id="vd-write-note" role="status" aria-live="polite">${writeLine(d)}</span>
        </div>`;
    return `
        <div class="vd-head">
            <input id="vd-name" class="vd-name" data-field="name" maxlength="${lim.name || 48}" value="${escAttr(pending.name ?? d.name)}" aria-label="Voice name" placeholder="Name this voice">
            <span class="vd-save-note" id="vd-save-note" role="status" aria-live="polite">${esc(saveNote)}</span>
            ${confirmDelete
                ? `<span class="vd-confirm">Delete this voice${d.voice_id ? ' and remove it from Your voices' : ''}?
                       <button class="vd-link danger" data-act="delete-yes">Delete</button>
                       <button class="vd-link" data-act="delete-no">Keep</button></span>`
                : `<button class="vd-icon-btn" data-act="delete" title="Delete this voice" aria-label="Delete this voice">
                       <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                   </button>`}
        </div>
        ${ideaBlock}
        <div class="vd-fields${direct ? ' vd-fields-one' : ''}">
            ${descBlock}
            <label class="vd-field">
                <span class="vd-label">Words it reads</span>
                <textarea id="vd-text" data-field="text" rows="5" maxlength="${lim.text || 600}"
                    placeholder="${escAttr(moodPassage || data.settings?.passage || data.default_passage || '')}">${esc(text)}</textarea>
                <span class="vd-hint">${moodPassage ? 'Leave empty for this mood’s own passage. What a voice reads shapes how it comes out as much as its description does.' : 'Leave empty for the standard passage.'}</span>
            </label>
        </div>
        <div class="vd-shape">
            <div class="vd-count vd-grow" title="${accents.length ? 'A real speaker whose accent the voice takes on' : 'No accent library on this box yet. Run scripts/fetch_accent_library.py on the server.'}">
                <label class="vd-label" for="vd-accent">Base voice</label>
                <div class="vd-accent-row">
                    <select id="vd-accent" data-field="accent" ${accents.length ? '' : 'disabled'}>${accentOptions(accents, chosenAccent)}</select>
                    <button class="vd-play${playing?.takeId === 'accent' ? ' playing' : ''}" data-act="play-accent" data-take="accent" ${accents.some((x) => x.id === chosenAccent) ? '' : 'disabled'} title="Hear the real speaker this accent comes from" aria-label="Hear the real speaker">
                        <svg class="vd-ico-play" viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><polygon points="6 4 20 12 6 20"/></svg>
                        <svg class="vd-ico-stop" viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="1.5"/></svg>
                    </button>
                </div>
            </div>
            ${direct ? '' : `<label class="vd-count" title="Push the voice toward a manner. None leaves it as described, which is the designer’s natural, pleasant voice. A mood changes what the voice is told and gives it fitting words to read. For most dark moods several versions of each take are made and the darkest kept, so a take is slower.">
                <span class="vd-label">Mood</span>
                <select id="vd-mood" data-field="mood"><option value="">None, as described</option>${moodOptions(data.moods || [], mood)}</select>
            </label>`}
            ${direct ? '' : `<label class="vd-count" title="How old the designed voice should sound">
                <span class="vd-label">Age</span>
                <select id="vd-age" data-field="age">${AGES.map(([n, label]) => `<option value="${n}"${n === Number(pending.age ?? d.age ?? 0) ? ' selected' : ''}>${label}</option>`).join('')}</select>
            </label>`}
            <label class="vd-count" title="The language of the words it reads">
                <span class="vd-label">Language</span>
                <select id="vd-language" data-field="language">${(data.languages || ['English']).map((l) => `<option${l === (pending.language ?? d.language) ? ' selected' : ''}>${esc(l)}</option>`).join('')}</select>
            </label>
            <label class="vd-count" title="How many takes to make this time">
                <span class="vd-label">Takes</span>
                <select id="vd-count">${Array.from({ length: lim.takes_per_run || 6 }, (_, i) => `<option${i + 1 === count ? ' selected' : ''}>${i + 1}</option>`).join('')}</select>
            </label>
        </div>
        <div class="vd-make">
            <span class="vd-make-status" id="vd-make-status" role="status" aria-live="polite">${esc(jobLine(d))}</span>
            ${running
                ? '<button class="vd-primary" data-act="stop">Stop</button>'
                : `<button class="vd-primary" data-act="generate" ${canMake ? '' : 'disabled'}>Generate</button>`}
        </div>
        <div class="vd-takes">
            <div class="vd-takes-head">
                <span class="vd-label">Takes</span>
                <span class="vd-takes-count">${d.takes.length ? `${d.takes.length} of ${lim.takes_kept || 24} kept` : ''}</span>
            </div>
            ${renderTakes(d, running)}
        </div>`;
}

function accentOptions(accents, chosen) {
    const groups = new Map();
    for (const a of accents) {
        if (!groups.has(a.accent)) groups.set(a.accent, []);
        groups.get(a.accent).push(a);
    }
    let html = `<option value=""${chosen ? '' : ' selected'}>Designed from the description</option>`;
    // A design carried from a box that had an accent this one lacks keeps saying so.
    if (chosen && !accents.some((a) => a.id === chosen)) html += `<option value="${escAttr(chosen)}" selected>Not in this library</option>`;
    for (const [name, list] of groups) {
        html += `<optgroup label="${escAttr(name)}">${list.map((a) => `<option value="${escAttr(a.id)}"${a.id === chosen ? ' selected' : ''}>${esc(a.label)}</option>`).join('')}</optgroup>`;
    }
    return html;
}

const AGES = [[-2, 'Child'], [-1, 'Young'], [0, 'As described'], [1, 'Older'], [2, 'Elderly']];
const ageLabel = (n) => (AGES.find(([v]) => v === Number(n)) || [0, ''])[1];

function moodOptions(moods, chosen) {
    const groups = new Map();
    for (const m of moods) {
        const g = m.group || 'Moods';
        if (!groups.has(g)) groups.set(g, []);
        groups.get(g).push(m);
    }
    return [...groups].map(([name, list]) => `<optgroup label="${escAttr(name)}">${list.map((m) => `<option value="${escAttr(m.key)}"${m.key === chosen ? ' selected' : ''}>${esc(m.label)}</option>`).join('')}</optgroup>`).join('');
}

function pitchOptions(max, chosen) {
    const out = [];
    for (let n = max; n >= -max; n--) {
        const label = n === 0 ? 'Unchanged' : `${Math.abs(n)} ${n < 0 ? 'deeper' : 'higher'}`;
        out.push(`<option value="${n}"${n === chosen ? ' selected' : ''}>${label}</option>`);
    }
    return out.join('');
}

// A voice saved from a dark-mood take is performed by the designer, not cloned.
const performed = (d) => {
    const t = d.takes.find((x) => x.id === d.voice_take);
    return !!t && !t.accent && (data?.moods || []).find((m) => m.key === t.mood)?.group === 'Dark';
};

// A designed take this high is cloned with a flutter once saved; a dark mood
// is performed by the designer instead, so it is not warned about.
const tooHigh = (t) => (t.hz || 0) >= 300 && !t.accent
    && (data?.moods || []).find((m) => m.key === t.mood)?.group !== 'Dark';

const accentLabel = (id) => (data?.accents || []).find((a) => a.id === id)?.label || '';

function takeNote(t) {
    const bits = [];
    if (t.accent) bits.push((accentLabel(t.accent) || 'speaker') + (t.blend ? ', blended' : ''));
    if (t.pitch) bits.push(`${Math.abs(t.pitch)} ${t.pitch < 0 ? 'deeper' : 'higher'}`);
    if (t.age && !t.accent) bits.push(ageLabel(t.age).toLowerCase());
    if (t.mood && !t.accent) bits.push(((data?.moods || []).find((m) => m.key === t.mood)?.label || t.mood).toLowerCase());
    return bits.length ? ` · ${bits.join(', ')}` : '';
}

const writerName = () => String(data?.writer || 'The chat model').split('/').pop();

function writeLine(d) {
    if (writing) return `${esc(writerName())} is writing how this voice sounds.`;
    if (writeNote) {
        const undo = writeUndo?.id === d.id ? ' <button class="vd-link" data-act="describe-undo">Undo</button>' : '';
        return esc(writeNote) + undo;
    }
    return `Say who the voice is in your own words. ${esc(writerName())} writes the description below, and you can change it before you generate.`;
}

async function describeIdea() {
    const d = design();
    const input = root.querySelector('#vd-idea');
    const idea = (input?.value || '').trim();
    if (!d || writing) return;
    if (!idea) { writeNote = 'Say who the voice is first.'; writeUndo = null; render(); root.querySelector('#vd-idea')?.focus(); return; }
    const id = d.id;
    const before = { id, description: pending.description ?? d.description, text: pending.text ?? d.text };
    pending.idea = idea;
    writing = true; writeNote = '';
    render();
    try {
        const res = await call('POST', '/describe', { idea, language: pending.language ?? d.language });
        if (currentId !== id) return;
        pending.description = res.description;
        const picked = (data.moods || []).find((m) => m.key === res.mood);
        // The writer names a mood for a dark character and clears it for anyone else.
        pending.mood = picked ? picked.key : '';
        writeUndo = before;
        writeNote = `Written by ${String(res.model || '').split('/').pop() || writerName()}.${picked ? ` Mood set to ${picked.label}.` : ''} Change anything you like.`;
    } catch (err) {
        writeNote = err.message;
        writeUndo = null;
    } finally {
        writing = false;
        if (currentId === id) { render(); await flushSave(); }
    }
}

async function undoDescribe() {
    if (!writeUndo || writeUndo.id !== currentId) return;
    pending.description = writeUndo.description;
    pending.text = writeUndo.text;
    writeUndo = null; writeNote = '';
    render();
    await flushSave();
}

function jobLine(d) {
    if (!job || job.design_id !== d.id) return '';
    if (job.state === 'running') {
        const where = job.worker ? ` on ${job.worker}` : '';
        if (job.making_room) return `Setting the speech engine aside to make room${where} · ${fmt(job.elapsed_s)}`;
        if (job.loading && !job.done) return `Loading the voice designer${where} · ${fmt(job.elapsed_s)}`;
        if (job.direct) return `Cloning take ${Math.min(job.done + 1, job.total)} of ${job.total}${where} · ${fmt(job.elapsed_s)}`;
        if (job.phase === 'accent') return `Giving take ${Math.min(job.done + 1, job.total)} of ${job.total} its accent${where} · ${fmt(job.elapsed_s)}`;
        if (job.designed != null && job.designed < job.total) return `Designing voice ${job.designed + 1} of ${job.total}${where} · ${fmt(job.elapsed_s)}`;
        if (job.candidates > 1) return `Making take ${Math.min(job.done + 1, job.total)} of ${job.total}${where}: version ${job.trying || 1} of ${job.candidates}, keeping the darkest · ${fmt(job.elapsed_s)}`;
        return `Making take ${Math.min(job.done + 1, job.total)} of ${job.total}${where} · ${fmt(job.elapsed_s)}`;
    }
    if (job.state === 'failed') return `Stopped: ${job.error}`;
    if (job.state === 'cancelled') return `Stopped after ${job.done} take${job.done === 1 ? '' : 's'}`;
    return `Made ${job.done} take${job.done === 1 ? '' : 's'} in ${fmt(job.elapsed_s)}`;
}

const fmt = (s) => { s = Math.max(0, Math.round(s || 0)); return s >= 60 ? `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s` : `${s}s`; };

function renderTakes(d, running) {
    if (!d.takes.length && !running) {
        return '<p class="vd-takes-empty">Nothing generated yet. Pick a base voice or describe one, then press Generate.</p>';
    }
    const nowMood = (data.moods || []).find((m) => m.key === d.mood && !d.accent);
    const now = { description: d.description, text: d.text || nowMood?.passage || data.settings?.passage || '' };
    const rows = [...d.takes].reverse().map((t, i) => {
        const inUse = d.voice_take === t.id && d.voice_id;
        const usesDescription = !t.accent || t.blend;
        const stale = (usesDescription && t.description && t.description !== now.description) || (t.text && t.text !== now.text)
            || (t.accent || '') !== (d.accent || '') || (!t.accent && (t.age || 0) !== (d.age || 0))
            || (!t.accent && (t.mood || '') !== (d.mood || ''))
            || (!!t.accent && !!t.blend !== !!d.blend);
        const isPlaying = playing?.takeId === t.id;
        return `
            <div class="vd-take${inUse ? ' in-use' : ''}">
                <button class="vd-play${isPlaying ? ' playing' : ''}" data-act="play" data-take="${t.id}" title="${isPlaying ? 'Stop' : 'Play'}" aria-label="${isPlaying ? 'Stop' : 'Play'} take ${d.takes.length - i}">
                    <svg class="vd-ico-play" viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><polygon points="6 4 20 12 6 20"/></svg>
                    <svg class="vd-ico-stop" viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="1.5"/></svg>
                </button>
                <span class="vd-take-name">Take ${d.takes.length - i}</span>
                <span class="vd-take-meta">${t.seconds}s${esc(takeNote(t))}${stale ? ' · earlier settings' : ''}${tooHigh(t) ? ` · <span class="vd-warn" title="This take sits at about ${t.hz} Hz, which is very high. The speech engine flutters on voices this high once saved. Another take, or a lower-voiced description, will speak more cleanly.">very high voice</span>` : ''}</span>
                ${inUse
                    ? '<span class="vd-badge">Saved voice</span>'
                    : `<button class="vd-use" data-act="use" data-take="${t.id}" title="${d.voice_id ? 'Replace the saved voice with this take' : 'Put this voice in Your voices'}">${d.voice_id ? 'Save this one instead' : 'Save this voice'}</button>`}
                <button class="vd-icon-btn" data-act="take-delete" data-take="${t.id}" title="${inUse ? 'This is the saved voice: save another take first' : 'Delete this take'}" aria-label="Delete take" ${inUse ? 'disabled' : ''}>
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
            </div>`;
    });
    if (running) {
        for (let n = job.done; n < job.total; n++) {
            rows.unshift(`<div class="vd-take pending"><span class="vd-spinner"></span><span class="vd-take-name">Take ${d.takes.length + (n - job.done) + 1}</span><span class="vd-take-meta">${n === job.done ? 'being made' : 'waiting'}</span></div>`);
        }
    }
    const foot = d.voice_id
        ? `<p class="vd-takes-foot">Saved as “${esc(d.name)}” in Your voices. Pick it on the <button class="vd-link" data-act="go-voices">Voices tab</button> and the expression, pace, speed and pitch sliders apply to it.${performed(d) ? ' Because it has a dark mood, the voice designer performs every line, carrying on from this take, so it speaks in character and starts a few seconds later than other voices.' : ''} <button class="vd-link" data-act="unpublish">Remove it from Your voices</button></p>`
        : '';
    return `<div class="vd-take-list">${rows.join('')}</div>${foot}`;
}

function renderDefaults() {
    const s = data.settings || {};
    const lim = data.limits || {};
    return `
        <div class="vd-head"><h3 class="vd-title">Defaults for new voices</h3>
            ${data.designs.length ? '' : '<button class="vd-link" data-act="defaults">Done</button>'}
            <span class="vd-save-note" id="vd-save-note" role="status" aria-live="polite">${esc(saveNote)}</span></div>
        <div class="vd-fields vd-fields-one">
            <label class="vd-field">
                <span class="vd-label">Standard passage</span>
                <textarea id="vd-set-passage" data-setting="passage" rows="5" maxlength="${lim.text || 600}">${esc(s.passage || '')}</textarea>
                <span class="vd-hint">What a voice reads when its own words are left empty. About fifteen seconds of plain speech works best.
                    <button class="vd-link" data-act="passage-reset">Restore the original</button></span>
            </label>
        </div>
        <div class="vd-make">
            <label class="vd-count"><span class="vd-label">Takes each time</span>
                <select id="vd-set-takes" data-setting="takes">${Array.from({ length: lim.takes_per_run || 6 }, (_, i) => `<option${i + 1 === (s.takes || 2) ? ' selected' : ''}>${i + 1}</option>`).join('')}</select></label>
            <label class="vd-count"><span class="vd-label">Language</span>
                <select id="vd-set-language" data-setting="language">${(data.languages || ['English']).map((l) => `<option${l === s.language ? ' selected' : ''}>${esc(l)}</option>`).join('')}</select></label>
        </div>
        <p class="vd-hint vd-about">Takes are made by the voice designer${data.designer?.worker ? ` on ${esc(data.designer.worker)}` : ''}. It loads when you generate and lets go of the graphics card after five idle minutes.</p>`;
}

// Re-rendering replaces the form, so the field being typed in is put back.
function captureFocus() {
    const el = document.activeElement;
    if (!el || !root.contains(el) || !el.id) return null;
    return { id: el.id, start: el.selectionStart, end: el.selectionEnd };
}
function restoreFocus(keep) {
    if (!keep) return;
    const el = root.querySelector(`#${keep.id}`);
    if (!el) return;
    el.focus({ preventScroll: true });
    try { if (keep.start != null) el.setSelectionRange(keep.start, keep.end); } catch { /* selects have no range */ }
}

function setSaveNote(text) {
    saveNote = text;
    const el = root?.querySelector('#vd-save-note');
    if (el) el.textContent = text;
}

// ─── Editing ─────────────────────────────────────────────────────────

function onInput(e) {
    const field = e.target.dataset?.field;
    const setting = e.target.dataset?.setting;
    if (field && currentId) {
        pending[field] = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setSaveNote('');
        clearTimeout(saveTimer);
        // A menu changes what the takes below are compared against, so the
        // list is redrawn; typing only saves, or the caret would jump.
        if (e.target.tagName === 'SELECT' || e.target.type === 'checkbox') flushSave().then(render);
        else saveTimer = setTimeout(flushSave, e.type === 'change' ? 0 : 700);
    } else if (setting) {
        clearTimeout(saveTimer);
        const body = { [setting]: e.target.value };
        saveTimer = setTimeout(async () => {
            try { adopt(await call('PUT', '/settings', body)); setSaveNote('Saved'); }
            catch (err) { setSaveNote(err.message); }
        }, e.type === 'change' ? 0 : 700);
    }
}

async function flushSave() {
    clearTimeout(saveTimer);
    const id = currentId;
    const body = pending;
    if (!id || !Object.keys(body).length) return true;
    pending = {};
    try {
        const res = await call('PUT', `/${id}`, body);
        adopt(res);
        setSaveNote('Saved');
        // Names show in the rail and in Your voices; refresh only those.
        const list = root.querySelector('.vd-list');
        if (list) list.innerHTML = renderList();
        if ('name' in body && design()?.voice_id) onVoicesChanged();
        return true;
    } catch (err) {
        pending = { ...body, ...pending };
        setSaveNote(err.message);
        return false;
    }
}

async function onClick(e) {
    const btn = e.target.closest('[data-act]');
    if (!btn || btn.disabled) return;
    const act = btn.dataset.act;
    errorLine = '';
    try {
        if (act === 'new') await createDesign({ name: uniqueName('New voice') });
        else if (act === 'starter') {
            const s = STARTERS[Number(btn.dataset.i)];
            await createDesign({ ...s, name: uniqueName(s.name) });
        } else if (act === 'open') {
            await flushSave();
            stopPlaying();
            currentId = btn.dataset.id; showDefaults = false; confirmDelete = false; saveNote = ''; writeNote = ''; writeUndo = null;
            render();
        } else if (act === 'defaults') {
            await flushSave();
            stopPlaying();
            showDefaults = !showDefaults; saveNote = '';
            render();
        } else if (act === 'passage-reset') {
            adopt(await call('PUT', '/settings', { passage: '' }));
            saveNote = 'Saved';
            render();
        } else if (act === 'delete') { confirmDelete = true; render(); }
        else if (act === 'delete-no') { confirmDelete = false; render(); }
        else if (act === 'delete-yes') await deleteDesign();
        else if (act === 'describe') await describeIdea();
        else if (act === 'describe-undo') await undoDescribe();
        else if (act === 'generate') await generate();
        else if (act === 'stop') await stopJob();
        else if (act === 'play') togglePlay(btn.dataset.take);
        else if (act === 'play-accent') {
            const id = root.querySelector('#vd-accent')?.value;
            if (id) togglePlay('accent', `accents/${encodeURIComponent(id)}`);
        }
        else if (act === 'use') await useTake(btn.dataset.take, btn);
        else if (act === 'take-delete') {
            if (playing?.takeId === btn.dataset.take) stopPlaying();
            const row = btn.closest('.vd-take');
            btn.disabled = true;
            // The row folds away while the server is asked, then the list is
            // redrawn underneath it, so nothing jumps.
            if (row) {
                row.style.height = `${row.offsetHeight}px`;
                void row.offsetHeight;
                row.classList.add('leaving');
            }
            const [res] = await Promise.all([
                call('DELETE', `/${currentId}/takes/${btn.dataset.take}`),
                new Promise((r) => setTimeout(r, row ? 220 : 0)),
            ]);
            adopt(res);
            render();
        } else if (act === 'go-voices') {
            document.querySelector('.voice-tab-btn[data-tab="voices"]')?.click();
        } else if (act === 'unpublish') {
            adopt(await call('DELETE', `/${currentId}/voice`));
            onVoicesChanged();
            render();
        }
    } catch (err) {
        errorLine = err.message;
        render();
    }
}

function uniqueName(base) {
    const names = new Set((data?.designs || []).map((d) => d.name));
    if (!names.has(base)) return base;
    let n = 2;
    while (names.has(`${base} ${n}`)) n++;
    return `${base} ${n}`;
}

async function createDesign(fields) {
    await flushSave();
    stopPlaying();
    const res = await call('POST', '', fields);
    adopt(res);
    currentId = res.design.id; showDefaults = false; confirmDelete = false; saveNote = '';
    render();
    const name = root.querySelector('#vd-name');
    name?.focus();
    name?.select();
}

async function deleteDesign() {
    stopPlaying();
    const had = design()?.voice_id;
    pending = {};
    adopt(await call('DELETE', `/${currentId}`));
    currentId = data.designs[0]?.id || null;
    confirmDelete = false; saveNote = '';
    if (had) onVoicesChanged();
    render();
}

// ─── Generating ──────────────────────────────────────────────────────

async function generate() {
    if (!(await flushSave())) return;
    const d = design();
    const speaker = (data.accents || []).some((x) => x.id === d.accent);
    if (!(speaker && !d.blend) && !d.description.trim()) {
        errorLine = 'Describe how the voice sounds first.';
        render();
        root.querySelector('#vd-description')?.focus();
        return;
    }
    const count = Number(root.querySelector('#vd-count')?.value) || 1;
    job = await call('POST', `/${d.id}/takes`, { count });
    render();
    pollJob();
}

function pollJob() {
    clearTimeout(jobTimer);
    jobTimer = setTimeout(async () => {
        if (!job) return;
        const before = job.done;
        try {
            job = await call('GET', `/jobs/${job.id}`);
        } catch (err) {
            job = { ...job, state: 'failed', error: err.message };
        }
        if (job.done !== before || job.state !== 'running') {
            try { adopt(await call('GET', '')); } catch { /* the next poll will */ }
            render();
        } else {
            const line = root?.querySelector('#vd-make-status');
            const d = design();
            if (line && d) line.textContent = jobLine(d);
        }
        if (job.state === 'running') pollJob();
    }, 1500);
}

async function stopJob() {
    if (!job) return;
    job = await call('DELETE', `/jobs/${job.id}`);
    // The take being made finishes and is kept; keep watching until it does.
    render();
    pollJob();
}

// ─── Listening and choosing ──────────────────────────────────────────

function stopPlaying() {
    if (!playing) return;
    playing.audio.pause();
    playing.audio.src = '';
    playing = null;
    root?.querySelectorAll('.vd-play.playing').forEach((b) => b.classList.remove('playing'));
}

function togglePlay(takeId, path = `${currentId}/takes/${takeId}`) {
    const same = playing?.takeId === takeId;
    stopPlaying();
    if (same) return;
    const audio = new Audio(`${API_BASE}/voice/designs/${path}/audio${authQuery()}`);
    playing = { takeId, audio };
    const done = () => { if (playing?.audio === audio) { playing = null; root?.querySelector(`.vd-play[data-take="${takeId}"]`)?.classList.remove('playing'); } };
    audio.addEventListener('ended', done);
    audio.addEventListener('error', done);
    root.querySelector(`.vd-play[data-take="${takeId}"]`)?.classList.add('playing');
    audio.play().catch(done);
}

async function useTake(takeId, btn) {
    btn.disabled = true;
    btn.textContent = 'Saving…';
    const res = await call('POST', `/${currentId}/voice`, { take: takeId });
    adopt(res);
    onVoicesChanged();
    render();
}
